import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function SafetyPage() {
  const { t } = useTranslation();

  const safetyTopics = [
    {
      title: "URL Verification",
      desc: "Always verify you are on the correct AllChinaBuy website. Check the URL carefully and avoid clicking suspicious links from emails or messages.",
      icon: "ri-link",
      color: "primary",
    },
    {
      title: "Seller Verification",
      desc: "Research seller ratings, reviews, and transaction history before purchasing. Avoid sellers with no reviews or suspiciously low prices.",
      icon: "ri-user-search-line",
      color: "accent",
    },
    {
      title: "QC Checking",
      desc: "Always request detailed quality control photos before approving items for shipment. Check for defects, sizing issues, and color accuracy.",
      icon: "ri-image-line",
      color: "secondary",
    },
    {
      title: "Payment Safety",
      desc: "Use secure payment methods provided by the platform. Never send money directly to sellers outside of the official platform.",
      icon: "ri-secure-payment-line",
      color: "primary",
    },
    {
      title: "Account Security",
      desc: "Use strong passwords, enable two-factor authentication if available, and never share your login credentials with anyone.",
      icon: "ri-shield-keyhole-line",
      color: "accent",
    },
    {
      title: "Shipping Risks",
      desc: "Understand shipping risks including delays, customs inspections, and potential damage. Consider insurance for valuable items.",
      icon: "ri-truck-line",
      color: "secondary",
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="pt-28 md:pt-32 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                {t("sectionSafety")}
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto">
                {t("sectionSafetyDesc")}
              </p>
            </div>

            <div className="space-y-8">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {safetyTopics.map((topic) => (
                  <div
                    key={topic.title}
                    className="p-5 rounded-2xl bg-background-100 border border-background-200/70 hover:border-primary-300 hover:shadow-sm transition-all"
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                      topic.color === "primary" ? "bg-primary-100" :
                      topic.color === "accent" ? "bg-accent-100" : "bg-secondary-100"
                    }`}>
                      <i className={`${topic.icon} ${
                        topic.color === "primary" ? "text-primary-600" :
                        topic.color === "accent" ? "text-accent-600" : "text-secondary-600"
                      }`} />
                    </div>
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">{topic.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{topic.desc}</p>
                  </div>
                ))}
              </div>

              <div className="p-6 md:p-8 rounded-2xl bg-accent-50/50 border border-accent-200/50">
                <div className="flex items-start gap-3">
                  <i className="ri-alarm-warning-line text-accent-600 text-xl mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">Scam Warning</h3>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                      Be aware of common scams in the shopping agent space. These include phishing websites, fake seller accounts, counterfeit products, and payment fraud.
                    </p>
                    <ul className="space-y-2 text-sm text-foreground-700">
                      <li className="flex items-start gap-2"><i className="ri-alert-line text-primary-500 mt-0.5 shrink-0" /> Never share login credentials or payment information outside official channels</li>
                      <li className="flex items-start gap-2"><i className="ri-alert-line text-primary-500 mt-0.5 shrink-0" /> Be suspicious of deals that seem too good to be true</li>
                      <li className="flex items-start gap-2"><i className="ri-alert-line text-primary-500 mt-0.5 shrink-0" /> Verify seller identity and reputation before purchasing</li>
                      <li className="flex items-start gap-2"><i className="ri-alert-line text-primary-500 mt-0.5 shrink-0" /> Always use the platform's built-in payment system</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">Safe Buying Practices</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    "Verify the website URL before logging in",
                    "Research products and sellers before purchasing",
                    "Request detailed QC photos before shipping",
                    "Use secure payment methods only",
                    "Keep records of all transactions and communications",
                    "Understand your country's import regulations",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-background-50">
                      <span className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 font-bold text-primary-600 text-xs">
                        {i + 1}
                      </span>
                      <span className="text-sm text-foreground-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-6 md:p-8 rounded-2xl bg-primary-500/10 border border-primary-200/50 text-center">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">Shop Safely with AllChinaBuy</h3>
                <p className="text-foreground-600 mb-6 text-sm">Follow our safety guides and shop with confidence.</p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-block px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors"
                >
                  {t("heroCtaPrimary")}
                </a>
              </div>

              <div className="p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">Related Resources</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Link to="/article/is-allchinabuy-legit" className="p-4 rounded-xl bg-background-50 border border-background-200/50 hover:border-primary-300 transition-all">
                    <h4 className="font-semibold text-sm text-foreground-900 hover:text-primary-700 transition-colors">{t("articleTitle2")}</h4>
                  </Link>
                  <Link to="/shipping" className="p-4 rounded-xl bg-background-50 border border-background-200/50 hover:border-primary-300 transition-all">
                    <h4 className="font-semibold text-sm text-foreground-900 hover:text-primary-700 transition-colors">{t("sectionShipping")}</h4>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}